import { isEqual } from 'lodash-es';
import { AnnotationFactory } from './AnnotationFactory';
const STACK_ACTION_CLEAR = 'clear';
const STACK_ACTION_ADD = 'add';
const STACK_ACTION_UNDO = 'undo';
const STACK_ACTION_REDO = 'redo';
const HIDE_BOX_INDIVIDUAL_TRUE = true;
const HIDE_BOX_INDIVIDUAL_FALSE = false;
const OPTIMIZE_THRESHOLD = 0.005;
const VERSION_1 = 1;
const VERSION_2 = 2;
const VERSION_3 = 3;
const VALUE_NOT_FOUND = -1;
export class AnnotationHelper {
    constructor(element, info, options) {
        this.element = element;
        this.info = Object.assign(Object.assign({}, info), { data: info.data.map((annotation, index) => (Object.assign(Object.assign({}, annotation), { id: index }))) });
        this.options = Object.assign({}, {
            append: true,
            create: false,
            devicePixelRatio: window.devicePixelRatio || 1,
        }, options);
        this._schema = null;
        // Initialize the used version
        this._detectMetaVersion();
        this._detectSchema();
        // History + result
        this.annotationStack = [];
        // Only init undo stack when creator is given
        if (options.create === true) {
            this._initUndoStack();
        }
        this.redoStack = [];
        this.renderer = null;
        this.creator = null;
        this.listeners = {};
        this._addRenderer();
        this.renderer.render();
        if (options.create === true && this._getMetaVersion() >= VERSION_3) {
            this._addCreator(options.creatorZoom || 1);
            this._renderTextAnnotations();
        }
    }
    /**
     * Get the meta version
     * @returns {number}
     * @private
     */
    _getMetaVersion() {
        if (Object.keys(this.info).indexOf('meta') === -1) {
            this.info.meta = {};
        }
        if (typeof this.info.meta.version !== 'number') {
            this.info.meta.version = this._detectMetaVersion();
        }
        return this.info.meta.version;
    }
    /**
     * Detect the meta version for older annotations
     * @returns {number}
     * @private
     */
    _detectMetaVersion() {
        const self = this;
        let version = VERSION_3;
        if (isVersion1()) {
            version = VERSION_1;
        }
        else if (isVersion2()) {
            version = VERSION_2;
        }
        return version;
        function isVersion1() {
            let result = false;
            if (self.info.data.length > 0) {
                const coordinate = self.info.data[0];
                const point = coordinate.points[0];
                // Old iPad annotation have x and y properties for each point, v3 annotation are a regular array
                if (point.hasOwnProperty('x') && point.hasOwnProperty('y')) {
                    result = true;
                }
            }
            return result;
        }
        function isVersion2() {
            let result = false;
            if (self.info.data.length > 0) {
                const coordinate = self.info.data[0];
                // Look for 'coords' 'normalized' in the coord -> Android old annotations, aka version 2
                if (coordinate.hasOwnProperty('coords') && coordinate.coords === 'normalized') {
                    result = true;
                }
            }
            return result;
        }
    }
    /**
     * Detect the version of the schema being used
     * @private
     */
    _detectSchema() {
        this.schema = AnnotationFactory.getSchema(this._getMetaVersion());
    }
    _initUndoStack() {
        this.annotationStack = this.info.data.map((item) => {
            if (!item)
                return;
            if (item.type === this.schema.TYPE_TEXT) {
                return Object.assign({}, item, {
                    type: this.schema.TYPE_TEXT_TMP,
                });
            }
            return item;
        });
    }
    save() {
        if (this.options.create === true) {
            this.info.data = this._getData();
            // this.info.data = this.info.data.map(function (annotation, index) {
            //     return Object.assign({}, annotation, { id: index });
            // });
            this._initUndoStack();
            const annotations = this.info.data.filter((annotation) => !(annotation.type === this.schema.TYPE_TEXT || annotation.type === this.schema.TYPE_TEXT_TMP));
            this.renderer.setAnnotations(annotations);
            this.renderer.render();
        }
    }
    _renderTextAnnotations() {
        const textAnnotations = this.info.data.filter((annotation) => annotation.type === this.schema.TYPE_TEXT);
        textAnnotations.forEach((textAnnotation) => {
            this.creator.restoreTextBox(textAnnotation, textAnnotation.id);
        });
    }
    /**
     * Add an annotations renderer
     * @param {boolean} hasCreator
     * @private
     */
    _addRenderer() {
        const annotations = this.options.create === true
            ? this.info.data.filter((annotation) => !(annotation.type === this.schema.TYPE_TEXT || annotation.type === this.schema.TYPE_TEXT_TMP))
            : this.info.data;
        // Check version here
        this.renderer = AnnotationFactory.getRenderer(this._getMetaVersion(), this.element, annotations, this);
    }
    /**
     * Add an annotations creator
     * @param {Number} zoomRatio
     * @private
     */
    _addCreator(zoomRatio) {
        // Check version here
        this.creator = AnnotationFactory.getCreator(this._getMetaVersion(), this.element, [], this);
        this.creator.setZoomRatio(zoomRatio);
        this.creator.on('annotation', function (annotation) {
            this._doStackAction.call(this, STACK_ACTION_ADD, annotation);
        }.bind(this));
        this.creator.on('settings-updated', function () {
            this._notify('update');
        }.bind(this));
    }
    /**
     * Execute action on history stack
     * @param action
     * @param annotation
     * @private
     */
    _doStackAction(action, annotation) {
        const self = this;
        let schema = this.getSchema();
        if (action === STACK_ACTION_CLEAR) {
            stackClear();
        }
        else if (action === STACK_ACTION_ADD) {
            stackAdd();
        }
        else if (action === STACK_ACTION_UNDO) {
            stackUndo();
        }
        else if (action === STACK_ACTION_REDO) {
            stackRedo();
        }
        else {
            throw new Error(`Unknown stack action ${action}`);
        }
        // Now render the annotations after the clear position (or from start)
        let annotations = this.annotationStack;
        let clearPosition = this.annotationStack.lastIndexOf(STACK_ACTION_CLEAR);
        if (clearPosition > VALUE_NOT_FOUND) {
            annotations = this.annotationStack.slice(clearPosition + 1);
        }
        if (this.options.create === true) {
            annotations = annotations.filter((annotation) => !(annotation.type === this.schema.TYPE_TEXT_TMP || annotation.type === this.schema.TYPE_TEXT));
        }
        this.renderer.setAnnotations(annotations);
        this.renderer.render();
        this._notify('update');
        function stackClear() {
            // Clear all annotations, clear redo stack as well
            self.annotationStack.push(STACK_ACTION_CLEAR);
            // manually hide al textboxes
            self.annotationStack.forEach((action) => {
                if (action.type === schema.TYPE_TEXT_TMP) {
                    self.creator.hideTextBox(action.id, HIDE_BOX_INDIVIDUAL_FALSE);
                }
            });
            self.redoStack = [];
        }
        function stackAdd() {
            // Add to annotations, clear redo stack
            if (annotation) {
                self.annotationStack.push(annotation);
                self.redoStack = [];
            }
            else {
                throw new Error(`Could not add annotation to the stack ${JSON.stringify(annotation)}`);
            }
        }
        function stackUndo() {
            // Pop from annotations, add to redo
            let undoAction = self.annotationStack.pop();
            if (undoAction) {
                // undo text action is hiding the textbox
                if (undoAction.type === schema.TYPE_TEXT_TMP) {
                    self.creator.hideTextBox(undoAction.id, HIDE_BOX_INDIVIDUAL_TRUE);
                }
                // show hidden textboxes
                else if (undoAction === STACK_ACTION_CLEAR) {
                    stackUndoClearAction();
                }
                self.redoStack.push(undoAction);
            }
            else {
                throw new Error(`Could not undo the action ${JSON.stringify(undoAction)}`);
            }
        }
        function stackUndoClearAction() {
            let lastClear = self.annotationStack.lastIndexOf(STACK_ACTION_CLEAR) + 1;
            let count = self.annotationStack.length;
            // show all HIDE_BOX_INDIVIDUAL_FALSE
            for (let index = lastClear; index < count; index++) {
                let annotationTmp = self.annotationStack[index];
                if (annotationTmp.type === schema.TYPE_TEXT_TMP) {
                    self.creator.showTextBox(annotationTmp.id, HIDE_BOX_INDIVIDUAL_FALSE);
                }
            }
        }
        function stackRedo() {
            // Pop from annotations, add to stack
            let redoAction = self.redoStack.pop();
            if (redoAction) {
                // redo text action is showing the hiding textbox
                if (redoAction.type === schema.TYPE_TEXT_TMP) {
                    self.creator.showTextBox(redoAction.id, HIDE_BOX_INDIVIDUAL_TRUE);
                }
                // show hidden textboxes
                else if (redoAction === STACK_ACTION_CLEAR) {
                    stackRedoClearAction();
                }
                self.annotationStack.push(redoAction);
            }
            else {
                throw new Error(`Could not redo the action ${JSON.stringify(redoAction)}`);
            }
        }
        function stackRedoClearAction() {
            let lastClear = self.annotationStack.lastIndexOf(STACK_ACTION_CLEAR) + 1;
            let count = self.annotationStack.length;
            // show all HIDE_BOX_INDIVIDUAL_FALSE
            for (let index = lastClear; index < count; index++) {
                let annotationTmp = self.annotationStack[index];
                if (annotationTmp.type === schema.TYPE_TEXT_TMP) {
                    self.creator.hideTextBox(annotationTmp.id, HIDE_BOX_INDIVIDUAL_FALSE);
                }
            }
        }
    }
    /**
     * Get the currently used schema
     * @returns {object}
     */
    getSchema() {
        if (this.creator) {
            return this.creator.getSchema();
        }
    }
    /**
     * Set the tool type of the creator
     * @param toolType
     */
    setToolType(toolType) {
        if (this.creator) {
            this.creator.setToolType(toolType);
        }
    }
    /**
     * Get the type of the current tool
     */
    getToolType() {
        if (this.creator) {
            return this.creator.getToolType();
        }
    }
    finish() {
        if (this.creator && typeof this.creator.finishTextAnnotations === 'function') {
            this.creator.finishTextAnnotations();
        }
    }
    /**
     * Set the tool size of the creator
     * @param toolSize
     */
    setToolSize(toolSize) {
        if (this.creator) {
            this.creator.setToolSize(toolSize);
        }
    }
    /**
     * Get the size of the current tool
     */
    getToolSize() {
        if (this.creator) {
            return this.creator.getToolSize();
        }
    }
    /**
     * Set the tool color of the creator
     * @param toolColor
     */
    setToolColor(toolColor) {
        if (this.creator) {
            this.creator.setToolColor(toolColor);
        }
    }
    /**
     * Get the color of the current tool
     */
    getToolColor() {
        if (this.creator) {
            return this.creator.getToolColor();
        }
    }
    /**
     * Get the color id of the active textbox
     */
    getActiveTextBoxColorId() {
        if (this.creator) {
            return this.creator.getActiveTextBoxColorId();
        }
    }
    /**
     * Get the size of the active textbox
     */
    getActiveTextBoxColorSizeIndex() {
        if (this.creator) {
            return this.creator.getActiveTextBoxColorSizeIndex();
        }
    }
    /**
     * Undo last action
     */
    undo() {
        if (this.canUndo()) {
            this._doStackAction.call(this, STACK_ACTION_UNDO);
        }
    }
    /**
     * Check if there is an action that can be undone
     * @returns {boolean}
     */
    canUndo() {
        return this.annotationStack.length > 0;
    }
    /**
     * Redo last undone action
     */
    redo() {
        if (this.canRedo()) {
            this._doStackAction.call(this, STACK_ACTION_REDO);
        }
    }
    /**
     * Check if there is an action that can be redone
     * @returns {boolean}
     */
    canRedo() {
        return this.redoStack.length > 0;
    }
    /**
     * Clear the annotations
     */
    clear() {
        if (this.canClear()) {
            this._doStackAction.call(this, STACK_ACTION_CLEAR);
        }
    }
    /**
     * Check if there are any annotations
     * @returns {boolean}
     */
    canClear() {
        var lastClear = this.annotationStack.lastIndexOf(STACK_ACTION_CLEAR);
        var annotationsSinceLastClear = this.annotationStack.slice(lastClear + 1);
        return annotationsSinceLastClear.length > 0;
    }
    /**
     * Reset the annotations to the initial value (discard newly added annotations)
     */
    reset() {
        // If you can't create annotations, there's nothing to reset
        if (this.options.create === true) {
            // hide all textboxes, they follow a different render() process
            this.annotationStack.forEach((action) => {
                const indexOfStackActionInMap = this.info.data.map((item) => item.id).indexOf(action.id);
                const isTempText = action.type === 'TEXT_TMP' && indexOfStackActionInMap === -1;
                if (isTempText) {
                    this.creator.hideTextBox(action.id, false);
                    delete this.creator.textBoxMap[action.id];
                }
            });
            // SPW-4077 when calling 'reset' after 'clear' text boxes do not show
            this.creator.finishTextAnnotations();
            for (var id in this.creator.textBoxMap) {
                this.creator.showTextBox(id, false);
            }
            this._initUndoStack();
            const annotations = this.info.data.filter((annotation) => !(annotation.type === this.schema.TYPE_TEXT || annotation.type === this.schema.TYPE_TEXT_TMP));
            this.renderer.setAnnotations(annotations);
            this.renderer.render();
        }
    }
    /**
     * Is the initial annotationData equal to the annotationStack
     */
    didAnnotationsChange() {
        if (this.options.create === true) {
            const original = this.info.data || [];
            const originalNonTextAnnotations = original.filter((annotation) => !(annotation.type === this.schema.TYPE_TEXT || annotation.type === this.schema.TYPE_TEXT_TMP));
            const originalTextAnnotations = original.filter((annotation) => annotation.type === this.schema.TYPE_TEXT);
            const newNonTextAnnotations = this.renderer.getAnnotations();
            const newTextAnnotations = this.annotationStack
                .filter((annotation) => annotation.type === this.schema.TYPE_TEXT_TMP || annotation.type === this.schema.TYPE_TEXT)
                .map((annotation) => {
                if (annotation && annotation.type === this.schema.TYPE_TEXT_TMP) {
                    if (this.creator &&
                        this.creator.textBoxMap &&
                        this.creator.textBoxMap[annotation.id] &&
                        this.creator.textBoxMap[annotation.id].isVisible()) {
                        annotation = this.creator.textBoxMap[annotation.id].getData();
                    }
                    return annotation;
                }
                return annotation;
            });
            return !(isEqual(originalNonTextAnnotations.map((i) => i.id), newNonTextAnnotations.map((i) => i.id)) && isEqual(originalTextAnnotations, newTextAnnotations));
        }
        return false;
    }
    /**
     * Check if we are currently creating/editing text
     * @returns {null|boolean}
     */
    isCreatingText() {
        if (this.creator) {
            return this.creator.isCreatingText();
        }
    }
    /**
     * Update canvas dimensions and position
     * @param {Number} creatorZoom optional
     */
    resize(creatorZoom) {
        this.renderer.resize();
        if (this.creator) {
            this.creator.resize(creatorZoom);
        }
    }
    /**
     * uninitialize all components, remove HtmlElements
     */
    destroy() {
        if (this.creator) {
            this.creator.destroy();
        }
        this.renderer.destroy();
    }
    /**
     * Return list of annotations
     */
    _getData() {
        const schema = this.getSchema();
        const annotations = this.renderer.getAnnotations();
        const textAnnotations = this.annotationStack.filter((annotation) => annotation.type === this.schema.TYPE_TEXT_TMP || annotation.type === this.schema.TYPE_TEXT);
        const filteredAnnotations = annotations
            .concat(textAnnotations)
            .map((annotation) => {
            if (annotation && annotation.type === schema.TYPE_TEXT_TMP) {
                if (this.creator &&
                    this.creator.textBoxMap &&
                    this.creator.textBoxMap[annotation.id] &&
                    this.creator.textBoxMap[annotation.id].isVisible()) {
                    annotation = this.creator.textBoxMap[annotation.id].getData();
                }
                return annotation;
            }
            return annotation;
        })
            .filter((annotation) => annotation.type !== schema.TYPE_TEXT_TMP);
        return this._optimize(filteredAnnotations);
    }
    getData() {
        return this._getData().map((annotation) => {
            const a = Object.assign({}, annotation);
            if (a.hasOwnProperty('id')) {
                delete a['id'];
            }
            return a;
        });
    }
    /**
     * Optimize annotation points
     * @param {array} annotations
     */
    _optimize(annotations) {
        // Only works if there is a schema
        if (this._schema === null) {
            return annotations;
        }
        function optimizePoints(points) {
            let filteredPoints = [];
            let pointCount = points.length;
            if (pointCount > 2) {
                filteredPoints.push(points[0]);
                for (let counter = 1; counter < pointCount - 1; counter++) {
                    let previousPoint = filteredPoints[filteredPoints.length - 1];
                    let currentPoint = points[counter];
                    let diffX = previousPoint[0] - currentPoint[0];
                    let diffY = previousPoint[1] - currentPoint[1];
                    if (Math.sqrt(diffX * diffX + diffY * diffY) > OPTIMIZE_THRESHOLD) {
                        filteredPoints.push(currentPoint);
                    }
                }
                filteredPoints.push(points[pointCount - 1]);
            }
            else {
                filteredPoints = points;
            }
            return filteredPoints;
        }
        annotations.forEach((annotation) => {
            if ([this._schema.TYPE_PEN, this._schema.TYPE_HIGHLIGHTER].indexOf(annotation.type) > VALUE_NOT_FOUND) {
                annotation.points = optimizePoints(annotation.points);
            }
        });
        return annotations;
    }
    /**
     * Add listeners for events in the AnnotationHelper
     * @param eventName
     * @param callback
     */
    on(eventName, callback) {
        if (!this.listeners.hasOwnProperty(eventName)) {
            this.listeners[eventName] = [];
        }
        this.listeners[eventName].push(callback);
    }
    /**
     * Notify listeners when a certain event happens
     * @param eventName
     * @param data
     * @private
     */
    _notify(eventName, data = null) {
        if (this.listeners.hasOwnProperty(eventName)) {
            notifyListeners(this.listeners[eventName]);
        }
        function notifyListeners(listeners) {
            for (let index in listeners) {
                if (listeners.hasOwnProperty(index)) {
                    try {
                        let callback = listeners[index];
                        callback.call(callback, data);
                    }
                    catch (e) {
                        // Do nothing
                    }
                }
            }
        }
    }
}
//# sourceMappingURL=AnnotationHelper.js.map